import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import ThemeOptions from "../../Layout/ThemeOptions";
import { STRATEGY_OPTIONS } from "../../constants/strategies";

export default function SystemSettingsPage() {
  const { selectedAccount } = useAccount();

  // ── System settings ──────────────────────────────────────────
  const [sysSettings, setSysSettings] = useState(null);
  const [sysEditing, setSysEditing] = useState(false);
  const [sysDraft, setSysDraft] = useState({});
  const [sysSaving, setSysSaving] = useState(false);

  // ── Rule engine defaults ──────────────────────────────────────
  const [ruleDefaults, setRuleDefaults] = useState(null);
  const [ruleEditing, setRuleEditing] = useState(false);
  const [ruleDraft, setRuleDraft] = useState({});
  const [ruleSaving, setRuleSaving] = useState(false);

  // ── Strategy user settings ────────────────────────────────────
  const [stratType, setStratType] = useState("PYRAMID_HEDGING");
  const [userSettings, setUserSettings] = useState(null);
  const [stratEditing, setStratEditing] = useState(false);
  const [stratDraft, setStratDraft] = useState({});
  const [stratSaving, setStratSaving] = useState(false);

  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (selectedAccount) {
      fetchSystemSettings();
      fetchStrategySettings();
      fetchRuleEngineDefaults();
    }
  }, [selectedAccount, stratType]);

  const fetchSystemSettings = async () => {
    try {
      const res = await axios.get("/settings/system", { params: { username: selectedAccount } });
      setSysSettings(res.data);
      setSysDraft(res.data);
    } catch {
      setSysSettings(null);
    }
  };

  const fetchRuleEngineDefaults = async () => {
    try {
      const res = await axios.get("/settings/rule-engine", { params: { username: selectedAccount } });
      setRuleDefaults(res.data);
      setRuleDraft(res.data);
    } catch {
      setRuleDefaults(null);
    }
  };

  const saveRuleEngineDefaults = async () => {
    try {
      setRuleSaving(true);
      await axios.put("/settings/rule-engine", { username: selectedAccount, ...ruleDraft });
      setRuleDefaults(ruleDraft);
      setRuleEditing(false);
      flash("Rule engine defaults saved.");
    } catch {
      flash("Failed to save.", false);
    } finally {
      setRuleSaving(false);
    }
  };

  const fetchStrategySettings = async () => {
    try {
      const res = await axios.get("/strategy/settings", {
        params: { username: selectedAccount, strategy_type: stratType }
      });
      setUserSettings(res.data.user_settings);
      setStratDraft(flattenValues(res.data.user_settings));
    } catch {
      setUserSettings(null);
    }
  };

  // Convert {key: {value, title, detail}} → {key: value} for editing
  const flattenValues = (obj) => {
    const flat = {};
    for (const [k, v] of Object.entries(obj || {})) {
      flat[k] = v.value;
    }
    return flat;
  };

  // Merge draft values back into the full setting objects
  const mergeValues = (original, draft) => {
    const merged = {};
    for (const [k, v] of Object.entries(original || {})) {
      merged[k] = { ...v, value: draft[k] ?? v.value };
    }
    return merged;
  };

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), ok ? 3000 : 8000);
  };

  const saveSysSettings = async () => {
    try {
      setSysSaving(true);
      const res = await axios.put("/settings/system", { username: selectedAccount, ...sysDraft });
      setSysSettings(sysDraft);
      setSysEditing(false);
      flash(res.data.warning ? `Saved, but: ${res.data.warning}` : "System settings saved.", !res.data.warning);
    } catch {
      flash("Failed to save.", false);
    } finally {
      setSysSaving(false);
    }
  };

  const saveStratSettings = async () => {
    try {
      setStratSaving(true);
      const merged = mergeValues(userSettings, stratDraft);
      const res = await axios.put("/settings/strategy-user", {
        username: selectedAccount,
        strategy_type: stratType,
        user_settings: merged
      });
      setUserSettings(merged);
      setStratEditing(false);
      flash(res.data.warning ? `Saved, but: ${res.data.warning}` : "Strategy settings saved.", !res.data.warning);
    } catch {
      flash("Failed to save.", false);
    } finally {
      setStratSaving(false);
    }
  };

  return (
    <div style={{ padding: 25, background: "#f4f6f9", minHeight: "100vh" }}>

      <h2 style={pageTitle}>System Settings</h2>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>
          {msg.text}
        </div>
      )}

      {/* ── SYSTEM SETTINGS CARD ── */}
      <Card title="General System Settings">
        {!sysSettings ? (
          <p style={emptyText}>No system settings found for this account.</p>
        ) : (
          <>
            <div style={grid2}>
              <Field
                label="Currency Symbol"
                editing={sysEditing}
                type="text"
                value={sysDraft.currency_symbol ?? ""}
                display={sysSettings.currency_symbol}
                onChange={(v) => setSysDraft(d => ({ ...d, currency_symbol: v }))}
              />
            </div>

            <div style={{ marginTop: 20, marginBottom: 8, fontWeight: 600, fontSize: 13, color: "#495057", borderBottom: "1px solid #f0f0f0", paddingBottom: 6 }}>
              Call Delta Constraints (Pyramid Hedging)
            </div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
              Controls the delta band used to select call options when building a Pyramid Hedging strategy.
            </div>
            <div style={grid2}>
              <Field
                label="Minimum Delta Permitted"
                editing={sysEditing}
                type="number"
                value={sysDraft.min_delta_permitted ?? ""}
                display={sysSettings.min_delta_permitted}
                onChange={(v) => setSysDraft(d => ({ ...d, min_delta_permitted: parseFloat(v) || v }))}
              />
              <Field
                label="Maximum Delta Permitted"
                editing={sysEditing}
                type="number"
                value={sysDraft.max_delta_permitted ?? ""}
                display={sysSettings.max_delta_permitted}
                onChange={(v) => setSysDraft(d => ({ ...d, max_delta_permitted: parseFloat(v) || v }))}
              />
            </div>

            <ActionRow
              editing={sysEditing}
              saving={sysSaving}
              onEdit={() => setSysEditing(true)}
              onSave={saveSysSettings}
              onCancel={() => { setSysEditing(false); setSysDraft(sysSettings); }}
            />
          </>
        )}
      </Card>

      {/* ── RULE ENGINE DEFAULTS CARD ── */}
      <Card title="Rule Engine Defaults">
        <p style={{ fontSize: 12, color: "#888", marginTop: 0, marginBottom: 16 }}>
          These values are applied as fallbacks when an individual rule does not
          specify the field. Override per-rule by editing the rule JSON directly.
        </p>
        {!ruleDefaults ? (
          <p style={emptyText}>No rule engine settings found for this account.</p>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13, color: "#495057", borderBottom: "1px solid #f0f0f0", paddingBottom: 6 }}>
              Idempotency &amp; Cooldown
            </div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
              Controls how long the engine suppresses duplicate suggestions for the same market state,
              and how long a rule must wait before it can fire again.
            </div>
            <div style={grid3}>
              <Field
                label="Idempotency TTL (minutes)"
                editing={ruleEditing}
                type="number"
                value={ruleDraft.idempotency_ttl_minutes ?? ""}
                display={ruleDefaults.idempotency_ttl_minutes}
                onChange={(v) => setRuleDraft(d => ({ ...d, idempotency_ttl_minutes: parseInt(v) || v }))}
              />
              <Field
                label="Cooldown (minutes)"
                editing={ruleEditing}
                type="number"
                value={ruleDraft.cooldown_minutes ?? ""}
                display={ruleDefaults.cooldown_minutes}
                onChange={(v) => setRuleDraft(d => ({ ...d, cooldown_minutes: parseInt(v) || v }))}
              />
              <Field
                label="Reject Cooldown (minutes)"
                editing={ruleEditing}
                type="number"
                value={ruleDraft.reject_cooldown_minutes ?? ""}
                display={ruleDefaults.reject_cooldown_minutes}
                onChange={(v) => setRuleDraft(d => ({ ...d, reject_cooldown_minutes: parseInt(v) || v }))}
              />
            </div>

            <div style={{ marginTop: 20, marginBottom: 8, fontWeight: 600, fontSize: 13, color: "#495057", borderBottom: "1px solid #f0f0f0", paddingBottom: 6 }}>
              Rule Grouping &amp; Priority
            </div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
              Rules are grouped by <code>group_name</code>. Within each group, only the top
              <code> group_max_fires</code> rules (lowest priority number) are executed per cycle.
            </div>
            <div style={grid3}>
              <Field
                label="Default Group Name"
                editing={ruleEditing}
                type="text"
                value={ruleDraft.group_name ?? ""}
                display={ruleDefaults.group_name}
                onChange={(v) => setRuleDraft(d => ({ ...d, group_name: v }))}
              />
              <Field
                label="Default Priority"
                editing={ruleEditing}
                type="number"
                value={ruleDraft.priority ?? ""}
                display={ruleDefaults.priority}
                onChange={(v) => setRuleDraft(d => ({ ...d, priority: parseInt(v) || v }))}
              />
              <Field
                label="Max Fires per Group"
                editing={ruleEditing}
                type="number"
                value={ruleDraft.group_max_fires ?? ""}
                display={ruleDefaults.group_max_fires}
                onChange={(v) => setRuleDraft(d => ({ ...d, group_max_fires: parseInt(v) || v }))}
              />
            </div>

            <ActionRow
              editing={ruleEditing}
              saving={ruleSaving}
              onEdit={() => setRuleEditing(true)}
              onSave={saveRuleEngineDefaults}
              onCancel={() => { setRuleEditing(false); setRuleDraft(ruleDefaults); }}
            />
          </>
        )}
      </Card>

      {/* ── LAYOUT CONFIGURATOR ── */}
      <ThemeOptions />

      {/* ── STRATEGY USER SETTINGS CARD ── */}
      <Card
        title="Strategy Settings"
        toolbar={
          <select
            value={stratType}
            onChange={(e) => { setStratType(e.target.value); setStratEditing(false); }}
            style={selectStyle}
          >
            {STRATEGY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        }
      >
        {!userSettings ? (
          <p style={emptyText}>No strategy settings found.</p>
        ) : (
          <>
            <div style={grid2}>
              {Object.entries(userSettings)
                .filter(([key]) => !["tail_risk_pct", "allow_tail_risk", "rollout_mode", "opening_call_delta", "opening_put_delta", "opening_lots"].includes(key))
                .map(([key, cfg]) => (
                <StratField
                  key={key}
                  fieldKey={key}
                  cfg={cfg}
                  editing={stratEditing}
                  value={stratDraft[key] ?? cfg.value}
                  onChange={(v) => setStratDraft(d => ({ ...d, [key]: v }))}
                />
              ))}
            </div>

            <ActionRow
              editing={stratEditing}
              saving={stratSaving}
              onEdit={() => setStratEditing(true)}
              onSave={saveStratSettings}
              onCancel={() => { setStratEditing(false); setStratDraft(flattenValues(userSettings)); }}
            />
          </>
        )}
      </Card>
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────── */

function Card({ title, children, toolbar }) {
  return (
    <div style={card}>
      <div style={cardHeader}>
        <h5 style={cardTitle}>{title}</h5>
        {toolbar && <div>{toolbar}</div>}
      </div>
      <div style={cardBody}>{children}</div>
    </div>
  );
}

function Field({ label, editing, type, options, value, display, onChange }) {
  return (
    <div style={fieldBox}>
      <div style={fieldLabel}>{label}</div>
      {editing ? (
        type === "select" ? (
          <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : type === "text" ? (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={input}
          />
        ) : (
          <input
            type="number"
            step="any"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={input}
          />
        )
      ) : (
        <div style={fieldValue}>{display ?? "—"}</div>
      )}
    </div>
  );
}

const THRESHOLD_TYPES   = ["static", "adaptive", "expression"];
const ADJUSTMENT_TYPES  = ["standard", "trim", "roll"];

function StratField({ fieldKey, cfg, editing, value, onChange }) {
  const isBool           = typeof cfg.value === "boolean";
  const isNumber         = typeof cfg.value === "number";
  const isEnum           = fieldKey === "threshold_type";
  const isAdjustmentType = fieldKey === "adjustment_type";
  const isHedgeInstrument = fieldKey === "hedge_instrument";
  const isLongText       = fieldKey === "threshold_expression";
  const isHedgeStyle     = fieldKey === "average_buy_price_to_call_strike_ratio";

  const hedgeVal = parseFloat(value ?? 1.05);
  const hedgeLabel = hedgeVal < 0.98
    ? "Bullish — call set above avg cost, more upside potential"
    : hedgeVal > 1.02
    ? "Bearish — tighter call hedge, generates more premium income"
    : "Balanced — equal focus on income and upside";
  const hedgeColor = hedgeVal < 0.98 ? "#4caf50" : hedgeVal > 1.02 ? "#e67e22" : "#545cd8";

  return (
    <div style={{ ...fieldBox, gridColumn: isLongText ? "1 / -1" : undefined }}>
      <div style={fieldLabel}>{cfg.title || fieldKey}</div>
      {cfg.detail && <div style={fieldDetail}>{cfg.detail}</div>}
      {editing ? (
        isHedgeStyle ? (
          <div style={{ marginTop: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <small style={{ color: "#4caf50", fontWeight: 600 }}>◀ Bullish</small>
              <span style={{ fontWeight: 700, color: "#545cd8" }}>{hedgeVal.toFixed(2)}</span>
              <small style={{ color: "#e67e22", fontWeight: 600 }}>Bearish ▶</small>
            </div>
            <input
              type="range"
              min="0.85"
              max="1.15"
              step="0.01"
              value={hedgeVal}
              onChange={(e) => onChange(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: "#545cd8" }}
            />
            <div style={{ textAlign: "center", marginTop: 4 }}>
              <small style={{ color: hedgeColor, fontSize: 11 }}>{hedgeLabel}</small>
            </div>
          </div>
        ) : isBool ? (
          <select
            value={String(value)}
            onChange={(e) => onChange(e.target.value === "true")}
            style={input}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : isEnum ? (
          <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
            {THRESHOLD_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : isAdjustmentType ? (
          <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
            {ADJUSTMENT_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : isHedgeInstrument ? (
          <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
            <option value="SYNTHETIC_STOCK">SYNTHETIC_STOCK</option>
            <option value="FUTURES" disabled>FUTURES (not yet available)</option>
          </select>
        ) : isLongText ? (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ ...input, fontFamily: "monospace", fontSize: 12 }}
          />
        ) : isNumber ? (
          <input
            type="number"
            step="any"
            value={value}
            onChange={(e) => onChange(isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
            style={input}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={input}
          />
        )
      ) : (
        isHedgeStyle
          ? <div style={{ ...fieldValue, color: hedgeColor }}>{hedgeVal.toFixed(2)} — {hedgeLabel.split(" — ")[0]}</div>
          : <div style={fieldValue}>{String(value)}</div>
      )}
    </div>
  );
}

function ActionRow({ editing, saving, onEdit, onSave, onCancel }) {
  return (
    <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
      {editing ? (
        <>
          <button onClick={onSave} disabled={saving} style={btnPrimary}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
        </>
      ) : (
        <button onClick={onEdit} style={btnSecondary}>Edit</button>
      )}
    </div>
  );
}

/* ── Styles ───────────────────────────────────────────────────── */

const pageTitle = { fontSize: 22, fontWeight: 700, marginBottom: 20, color: "#2c3e50" };

const toast = {
  position: "fixed", top: 20, right: 20, zIndex: 9999,
  padding: "10px 20px", borderRadius: 8, color: "#fff",
  fontWeight: 600, fontSize: 14
};

const card = {
  background: "#fff", borderRadius: 12, marginBottom: 24,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
};
const cardHeader = {
  padding: "16px 20px", borderBottom: "1px solid #f0f0f0",
  display: "flex", alignItems: "center", justifyContent: "space-between"
};
const cardTitle = { margin: 0, fontSize: 15, fontWeight: 600, color: "#2c3e50" };
const cardBody = { padding: 20 };

const grid2 = {
  display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16
};

const grid3 = {
  display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16
};

const fieldBox = { display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel = { fontSize: 12, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" };
const fieldDetail = { fontSize: 12, color: "#888", marginBottom: 4, lineHeight: "1.4" };
const fieldValue = { fontSize: 14, color: "#2c3e50", fontWeight: 500, padding: "6px 0" };

const input = {
  padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc",
  fontSize: 13, width: "100%"
};
const selectStyle = {
  padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13
};

const btnPrimary = {
  padding: "8px 18px", borderRadius: 8, border: "none",
  background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600
};
const btnSecondary = {
  padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc",
  background: "#fff", color: "#333", cursor: "pointer", fontSize: 13
};

const emptyText = { color: "#888", fontSize: 13 };
